import { useEffect, useMemo, useState } from 'react';
import { AppDispatch, RootState } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { IconButton, Stack, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TablePagination from '@mui/material/TablePagination';

import Colors from 'constants/colors';
import { tableOffset } from 'appConfig';

import { resetTablePage, setTableOffset, updateTableState } from 'redux/slices/common/appSlice';
import { get } from 'utils/helpers';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import CustomPagination from 'components/Common/CustomPagination';
import NoResults from './NoResultsText';

export type Align = 'left' | 'right' | 'inherit' | 'justify' | 'center';
export interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: Align;
    format?: (value: number) => string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row shape varies per consumer
    render?: ({ value, data }: any) => React.ReactNode;
    path?: string;
}

export interface Object {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic row dictionary, shape varies per consumer
    [key: string]: any;
}

interface StickyHeadTableProps {
    data: Object[];
    columns: Column[];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- other consumers (ActivityContainerTable, BasketActivityContainer) also type this as Function; narrowing here alone breaks their assignability
    navigationHandler: Function;
    successValue?: string;
    showDeleteCell?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row shape varies per consumer
    onDeleteClick?: (value: any) => void;
    unStyled?: boolean;
    defaultPagination?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- shape varies per consumer's pagination payload
    loadData?: any;
    onTemplatebuttonclick?: () => void;
    getStartedTemplate?: boolean;
    templateButtonText?: string;
    templateTitle?: string;
    parentOffset?: number;
    loading?: boolean;
    totalCount?: number;
    serverSidePagination?: boolean; // REMOVE THIS AND OPTIMIZE PAGINATION OF TABLE
    showLoading?: boolean;
    /** Rows aren't clickable (no detail screen to go to) — hide the trailing arrow + pointer/hover cues. */
    disableRowNavigation?: boolean;
    /** Empty-state text; defaults to NoResults' "No results found.". `null` = render no empty state
     *  (the screen is already explaining why the list is empty, e.g. an error alert). */
    emptyMessage?: string | null;
}

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `keyof any` is the standard TS idiom for a property-key-like generic constraint
function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function StickyHeadTable({
    data,
    columns,
    navigationHandler,
    successValue,
    showDeleteCell,
    onDeleteClick,
    unStyled,
    defaultPagination = true,
    loadData,
    onTemplatebuttonclick,
    getStartedTemplate,
    templateButtonText,
    templateTitle,
    parentOffset,
    loading,
    totalCount,
    serverSidePagination,
    showLoading = false,
    disableRowNavigation = false,
    emptyMessage,
}: StickyHeadTableProps) {
    const dispatch = useDispatch<AppDispatch>();
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<string | null>(null);
    const { tableState } = useSelector((state: RootState) => ({ tableState: state.app.table }));
    const { pageSize, pageNumber, offset } = tableState || {};
    if (!serverSidePagination) {
        data = data?.slice(pageNumber * pageSize, pageNumber * pageSize + pageSize);
    }

    useEffect(() => {
        return () => {
            dispatch(resetTablePage());
        };
    }, [dispatch]);

    useEffect(() => {
        if (typeof parentOffset === 'number') {
            dispatch(setTableOffset(parentOffset));
        }
    }, [parentOffset, dispatch]);

    // Kept for the disabled column sorting below (see the commented TableSortLabel in the header).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const sortedData = useMemo(() => {
        if (!orderBy) return data;
        return data.sort(getComparator(order, orderBy));
    }, [data, order, orderBy]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSortRequest = (columnId: string) => {
        const isAsc = orderBy === columnId && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(columnId);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        const _offset = pageNumber > newPage ? offset - tableOffset : offset + tableOffset;
        const updatedPagination = { pageSize, offset: _offset, pageNumber: newPage };
        if (loadData) loadData(updatedPagination);
        dispatch(updateTableState(updatedPagination));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pageSize = Number(event.target.value);
        const updatedTableState = { ...tableState, pageSize, pageNumber: 0, offset: 0 };
        dispatch(updateTableState(updatedTableState));
        if (loadData) loadData(updatedTableState);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row value shape varies per consumer
    const getStyles = (rowValue: any) => {
        if (unStyled) {
            return {};
        }
        return { margin: '10px', backgroundColor: rowValue === 'Balance Withdraw' ? '#142456' : '#F2F2F6' };
    };
    if (loading && showLoading) {
        return <MuiLoader width="100%" height={400} loading={loading} />;
    }
    if (!data?.length && getStartedTemplate) {
        return (
            <>
                <Stack width="100%" height="200px" justifyContent="center" alignItems="center">
                    <Typography sx={{ color: 'gray', fontSize: 35 }}>{templateTitle || "Let's get started!"}</Typography>
                    <Button
                        backgroundColor="none"
                        variant="outlined"
                        title={templateButtonText}
                        onClick={onTemplatebuttonclick}
                        sx={{ mt: 2, fontSize: 18 }}
                    />
                </Stack>
            </>
        );
    }
    if (!data?.length && !loading) {
        return (
            <>
                {emptyMessage !== null && <NoResults message={emptyMessage} />}
                {offset > 0 && (
                    <CustomPagination
                        offset={offset}
                        pageSize={pageSize}
                        total={totalCount || 0}
                        onPreviousPress={(e) => offset > 0 && handleChangePage(e, pageNumber - 1)}
                        onNextPress={(e) => handleChangePage(e, pageNumber + 1)}
                    />
                )}
            </>
        );
    }
    return (
        <>
            <TableContainer sx={{ minHeight: '400px' }}>
                <Table aria-label="sticky table" style={{ borderSpacing: '0 0.5rem', border: 0 }}>
                    <TableHead>
                        <TableRow
                            sx={{
                                '&.MuiTableRow-root': {
                                    'th:first-child': {
                                        borderRadius: '5px 0 0 5px',
                                    },
                                    'th:last-child': {
                                        borderRadius: '0 5px 5px 0',
                                    },
                                },
                            }}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    size="medium"
                                    key={column.id}
                                    align={column?.align || 'left'}
                                    style={{
                                        minWidth: column.minWidth,
                                        fontSize: 16,
                                        backgroundColor: Colors.tableRow,
                                        lineHeight: 1,
                                        color: '#6B7280',
                                    }}
                                >
                                    {/* Sorting disable for now can be enabled later and fixed */}
                                    {/* <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    // onClick={() => handleSortRequest(column.id)}
                  >
                    {column.label}
                  </TableSortLabel> */}
                                    {column.label}
                                </TableCell>
                            ))}
                            {showDeleteCell && <TableCell style={{ backgroundColor: '#F2F2F6' }}></TableCell>}{' '}
                            {/* This cell is for the delete icon */}
                            {!disableRowNavigation && <TableCell style={{ backgroundColor: '#F2F2F6' }}></TableCell>}{' '}
                            {/* This cell is for the arrow icon */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((row, rowIndex) => {
                            const rowValue = row?.['from'];
                            return (
                                <TableRow
                                    onClick={() => {
                                        if (disableRowNavigation) return;
                                        navigationHandler(row);
                                    }}
                                    sx={{
                                        '&.MuiTableRow-root': {
                                            '& td:first-child': {
                                                borderTopLeftRadius: '5px',
                                                borderBottomLeftRadius: '5px',
                                            },
                                            '& td:last-child': {
                                                borderTopRightRadius: '5px',
                                                borderBottomRightRadius: '5px',
                                            },
                                            '&:hover': {
                                                backgroundColor: rowValue === 'Balance Withdraw' ? '#0A1640' : '#E5E7EB',
                                                cursor: disableRowNavigation ? 'default' : 'pointer',
                                            },
                                        },
                                        ...getStyles(rowValue),
                                    }}
                                    hover
                                    role="checkbox"
                                    tabIndex={-1}
                                    key={rowIndex}
                                >
                                    {columns.map((column) => {
                                        const value = column.render
                                            ? column.render({ value: get(row, column.id, ''), data: row })
                                            : get(row, column?.path || column.id, '');
                                        return (
                                            <TableCell
                                                style={{
                                                    fontSize: 16,
                                                    fontWeight: 400,
                                                    color:
                                                        value === 'Pending'
                                                            ? '#DC883C'
                                                            : value === successValue
                                                              ? '#00A76A'
                                                              : rowValue === 'Balance Withdraw'
                                                                ? 'white'
                                                                : '#1F2937',
                                                }}
                                                key={column.id}
                                                align={column.align || 'left'}
                                            >
                                                {column.format && typeof value === 'number' ? column.format(value) : value}
                                            </TableCell>
                                        );
                                    })}
                                    {showDeleteCell && (
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (onDeleteClick) onDeleteClick(row);
                                                }}
                                                sx={{ width: 24, height: 24 }}
                                            >
                                                <DeleteForeverIcon fontSize="small" htmlColor="#D7172F" style={{ cursor: 'pointer' }} />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                    {!disableRowNavigation && (
                                        <TableCell align="right">
                                            <IconButton sx={{ height: 30, width: 30 }}>
                                                <ArrowForwardIosIcon
                                                    htmlColor={rowValue === 'Balance Withdraw' ? 'white' : 'black'}
                                                    fontSize="small"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {!defaultPagination ? (
                <CustomPagination
                    offset={offset}
                    pageSize={pageSize}
                    total={data.length || 0}
                    onPreviousPress={(e) => handleChangePage(e, pageNumber - 1)}
                    onNextPress={(e) => handleChangePage(e, pageNumber + 1)}
                />
            ) : (
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount || data?.length}
                    rowsPerPage={pageSize}
                    page={pageNumber}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}
        </>
    );
}
